import { FC, useEffect, useRef, useState } from 'react';

import { push } from 'connected-react-router';
import { getAuctionEditUrl } from 'utils/auction';
import {
    CAMPAIGN_CREATION_WIZARD_HISTORY_STATE,
    CampaignCreationWizardCpcMode,
    CampaignCreationWizardStep,
    getCampaignCreationWizardProgress,
} from 'utils/campaignCreationWizardFlow/campaignCreationWizardFlow';
import {
    CAMPAIGN_CREATE_URL,
    CAMPAIGN_QUICK_CREATE_URL, // exp_PORTFOLIO-61587_next_line
    CAMPAIGN_VACANCY_OF_THE_DAY_CREATE_URL, // exp_PORTFOLIO-58018_next_line
    CAMPAIGN_VR_CREATE_URL,
} from 'utils/constants/app';
import { CampaignProductType, CampaignType } from 'utils/constants/campaign';
import { Settings } from 'utils/constants/dynamicSettings';
import { MagritteNotificationName } from 'utils/constants/notification';
import { CURRENT_ACCOUNT_ID_STORAGE } from 'utils/constants/user';

import creationWizardProductTypeBackButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/campaign_menu/main_page/creation_wizard_product_type_back_button_click';
import creationWizardProductTypeNextButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/campaign_menu/main_page/creation_wizard_product_type_next_button_click';
import creationWizardToCampaignsButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/campaign_menu/main_page/creation_wizard_to_campaigns_button_click';
import creationWizardVacancyNextButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/campaign_menu/main_page/creation_wizard_vacancy_next_button_click';
import linkNextButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/campaign_menu/main_page/link_next_button_click';
import { quickStartForeignAreaErrorShownRaw } from '@hh.ru/analytics-js-events/build/clickme/employer/common/quick_start_foreign_area_error_shown';
import { sessionStorageWrapper } from '@hh.ru/browser-api-utils';
import { ActionBar, Button, ProgressBar, useBreakpoint } from '@hh.ru/magritte-ui';

import useTrls from 'hooks/translation/useTrls';
import { useDashboardRoute } from 'hooks/useDashboardRoute';
import useSettings from 'hooks/useSettings';

import { fetchWizardBannerLinkCheck } from 'components/CampaignCreationWizard/CampaignCreationWizardActionBar/fetchWizardBannerLinkCheck';
import { fetchWizardCampaignOptions } from 'components/CampaignCreationWizard/CampaignCreationWizardActionBar/fetchWizardCampaignOptions';
import { fetchWizardExistAdvCampaigns } from 'components/CampaignCreationWizard/CampaignCreationWizardActionBar/fetchWizardExistAdvCampaigns';

import { setIsVacancyForeignArea } from 'store/autoReducers';
import {
    CampaignTarget,
    goBackToPromotionObjectStep,
    resetCampaignCreationWizard,
    selectCampaignCreationWizardActiveStep,
    selectIsPromotionObjectStepValid,
    selectWizardCampaignOptions,
    selectWizardCampaignOptionsIsLoading,
    selectWizardCampaignTarget,
    selectWizardCpcMode,
    selectWizardSelectedFilterValues,
    selectWizardSelectedProductType,
    selectWizardSelectedVacancyForeignArea,
    selectWizardSelectedVacancyId,
    selectWizardSelectedVacancyName,
    selectWizardVacancies,
    selectWizardVacanciesIsLoading,
    selectWizardVacancySearchValue,
    selectWizardWebsiteUrl,
    setCampaignCreationWizardActiveStep,
    setWizardWebsiteUrl,
    WizardCpcMode,
} from 'store/models/campaignCreationWizard';
import { setMainMenuLink, setMainMenuVacancy } from 'store/models/mainMenu';
import { addMagritteNotification } from 'store/models/notification';
import { setRequisitesFormOpenWithParams, setWrongRequisitesModalOpen } from 'store/models/requisites';
import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/CampaignCreationWizardFlow/CampaignCreationWizardProgress.module.less';

const TrlKeys = {
    toAds: 'CampaignCreationWizard.actionBar.toAds',
    next: 'CampaignCreationWizard.actionBar.next',
    back: 'CampaignCreationWizard.actionBar.back',
    goToEdit: 'CampaignCreationWizard.actionBar.goToEdit',
    requisitesTitleFill: 'adCreateMenu.requisites.title.fill',
    requisitesTitleCheck: 'adCreateMenu.requisites.title.check',
    requisitesButtonNext: 'adCreateMenu.requisites.button.next',
};

export const CampaignCreationWizardActionBar: FC = () => {
    const dispatch = useDispatch();
    const { isXS, isMobile } = useBreakpoint();
    const trl = useTrls();
    const settings = useSettings();
    const dashboardRoute = useDashboardRoute();
    const activeStep = useSelector(selectCampaignCreationWizardActiveStep);
    const isPromotionObjectStepValid = useSelector(selectIsPromotionObjectStepValid);
    const campaignTarget = useSelector(selectWizardCampaignTarget);
    const selectedVacancyId = useSelector(selectWizardSelectedVacancyId);
    const selectedVacancyName = useSelector(selectWizardSelectedVacancyName);
    const websiteUrl = useSelector(selectWizardWebsiteUrl);
    const vacancySearchValue = useSelector(selectWizardVacancySearchValue);
    const selectedFilterValues = useSelector(selectWizardSelectedFilterValues);
    const vacancies = useSelector(selectWizardVacancies);
    const vacanciesIsLoading = useSelector(selectWizardVacanciesIsLoading);
    const selectedProductType = useSelector(selectWizardSelectedProductType);
    const selectedCpcMode = useSelector(selectWizardCpcMode);
    const campaignOptions = useSelector(selectWizardCampaignOptions);
    const campaignOptionsIsLoading = useSelector(selectWizardCampaignOptionsIsLoading);
    const isForeignArea = useSelector(selectWizardSelectedVacancyForeignArea);
    const isRequisitesFilled = useSelector((state) => state.requisites.isRequisitesFilled);
    const isInBlocklistCampaignCreating = settings(Settings.BlocklistCampaignCreating) as boolean;

    const [isNextLoading, setIsNextLoading] = useState(false);
    const nextAbortControllerRef = useRef<AbortController | null>(null);

    const isFirstStep = activeStep === CampaignCreationWizardStep.PromotionObject;
    const isSecondStep = activeStep === CampaignCreationWizardStep.PromotionType;
    const isFiltered =
        vacancySearchValue !== '' || selectedFilterValues.areas.length > 0 || selectedFilterValues.users.length > 0;

    useEffect(
        () => () => {
            const abortController = nextAbortControllerRef.current;
            nextAbortControllerRef.current = null;
            abortController?.abort();
        },
        []
    );

    const createNextAbortController = () => {
        nextAbortControllerRef.current?.abort();

        const abortController = new AbortController();
        nextAbortControllerRef.current = abortController;
        setIsNextLoading(true);
        return abortController;
    };

    const openPageCreateCampaign = () => {
        dispatch(push(CAMPAIGN_CREATE_URL, CAMPAIGN_CREATION_WIZARD_HISTORY_STATE));
    };

    const openRequisitesModal = (title: string) => {
        dispatch(
            setRequisitesFormOpenWithParams({
                title,
                buttonName: trl(TrlKeys.requisitesButtonNext),
                callback: openPageCreateCampaign,
            })
        );
    };

    const navigateToCpcCreate = async (abortSignal?: AbortSignal): Promise<void> => {
        if (isInBlocklistCampaignCreating) {
            dispatch(setWrongRequisitesModalOpen(true));
            return;
        }
        if (!isRequisitesFilled) {
            openRequisitesModal(trl(TrlKeys.requisitesTitleFill));
            return;
        }
        await fetchWizardExistAdvCampaigns(abortSignal)
            .then((existAdvertisingCampaigns) => {
                if (existAdvertisingCampaigns === null) {
                    return;
                }
                if (existAdvertisingCampaigns) {
                    openPageCreateCampaign();
                } else {
                    openRequisitesModal(trl(TrlKeys.requisitesTitleCheck));
                }
            })
            .catch(() => {
                dispatch(addMagritteNotification(MagritteNotificationName.SomethingWentWrong));
            });
    };

    const handleWebsiteNavigation = async (
        advertisingServiceType: CampaignType,
        abortSignal: AbortSignal
    ): Promise<void> => {
        if (advertisingServiceType === CampaignType.Information) {
            openPageCreateCampaign();
            return;
        }
        await navigateToCpcCreate(abortSignal);
    };

    const handleNextWebsite = () => {
        linkNextButtonClick();
        const abortController = createNextAbortController();

        void dispatch(fetchWizardBannerLinkCheck(websiteUrl, abortController.signal))
            .then(async (result) => {
                if (!result) {
                    return;
                }
                if (result.isVacancyUrl) {
                    dispatch(setCampaignCreationWizardActiveStep(CampaignCreationWizardStep.PromotionType));
                } else {
                    await handleWebsiteNavigation(result.advertisingServiceType, abortController.signal);
                }
            })
            .catch(() => {
                // ошибка обработана внутри fetchWizardBannerLinkCheck
            })
            .finally(() => {
                if (nextAbortControllerRef.current === abortController) {
                    nextAbortControllerRef.current = null;
                    setIsNextLoading(false);
                }
            });
    };

    const handleNextVacancy = () => {
        if (selectedVacancyId === null) {
            return;
        }
        creationWizardVacancyNextButtonClick({ vacancyId: selectedVacancyId });
        const abortController = createNextAbortController();

        void dispatch(fetchWizardCampaignOptions(selectedVacancyId, abortController.signal))
            .then(() => {
                if (!abortController.signal.aborted) {
                    dispatch([
                        setWizardWebsiteUrl(''),
                        setMainMenuLink(null),
                        setCampaignCreationWizardActiveStep(CampaignCreationWizardStep.PromotionType),
                    ]);
                }
            })
            .catch(() => {
                // ошибка обработана внутри fetchWizardCampaignOptions
            })
            .finally(() => {
                if (nextAbortControllerRef.current === abortController) {
                    nextAbortControllerRef.current = null;
                    setIsNextLoading(false);
                }
            });
    };

    const handleNextOnPromotionTypeStep = () => {
        if (selectedProductType === null || !campaignOptions?.productTypes.includes(selectedProductType)) {
            return;
        }
        creationWizardProductTypeNextButtonClick({
            vacancyId: selectedVacancyId,
            productType: selectedProductType,
            cpcMode: selectedCpcMode ?? '',
        });
        // exp_PORTFOLIO-58018_next_line
        dispatch(setMainMenuVacancy({ vacancyId: selectedVacancyId, vacancyName: selectedVacancyName ?? undefined }));
        switch (selectedProductType) {
            case CampaignProductType.SearchAuction:
                dispatch(push(getAuctionEditUrl(selectedVacancyId), CAMPAIGN_CREATION_WIZARD_HISTORY_STATE));
                return;
            case CampaignProductType.Cpc: {
                const isQuickMode = selectedCpcMode === WizardCpcMode.Quick;
                if (isQuickMode && isForeignArea) {
                    quickStartForeignAreaErrorShownRaw();
                    dispatch(addMagritteNotification(MagritteNotificationName.AdQuickCreateWithForeignArea));
                    return;
                }
                dispatch(setIsVacancyForeignArea(isForeignArea));
                dispatch(
                    push(
                        isQuickMode ? CAMPAIGN_QUICK_CREATE_URL : CAMPAIGN_CREATE_URL,
                        CAMPAIGN_CREATION_WIZARD_HISTORY_STATE
                    )
                );
                return;
            }
            // exp_PORTFOLIO-58018_start
            case CampaignProductType.Vr:
                dispatch(push(CAMPAIGN_VR_CREATE_URL, CAMPAIGN_CREATION_WIZARD_HISTORY_STATE));
                return;
            // exp_PORTFOLIO-58018_end
            // exp_PORTFOLIO-61587_start
            case CampaignProductType.VacancyOfTheDay:
                dispatch(push(CAMPAIGN_VACANCY_OF_THE_DAY_CREATE_URL, CAMPAIGN_CREATION_WIZARD_HISTORY_STATE));
                return;
            // exp_PORTFOLIO-61587_end
            default: {
                const unexpectedProductType: never = selectedProductType;
                throw new Error(unexpectedProductType);
            }
        }
    };

    const handleNext = () => {
        if (isFirstStep) {
            if (campaignTarget === CampaignTarget.Website) {
                handleNextWebsite();
            } else {
                handleNextVacancy();
            }
            return;
        }
        if (isSecondStep) {
            handleNextOnPromotionTypeStep();
            return;
        }
        dispatch(setCampaignCreationWizardActiveStep(activeStep + 1));
    };

    const handleBackClick = () => {
        creationWizardProductTypeBackButtonClick();
        dispatch(goBackToPromotionObjectStep());
    };

    const handleToAdsClick = () => {
        creationWizardToCampaignsButtonClick();
        dispatch(resetCampaignCreationWizard(null));
        dispatch(push(dashboardRoute));
    };

    const currentAccountId = sessionStorageWrapper.getItem(CURRENT_ACCOUNT_ID_STORAGE);
    const auctionCampaign = campaignOptions?.auctionProductData?.auctionCampaign;
    const isAuctionWarning =
        !!(currentAccountId && auctionCampaign) &&
        auctionCampaign.campaignAccountInfo.id !== Number(currentAccountId) &&
        selectedProductType === CampaignProductType.SearchAuction;

    const isSelectedProductActive =
        selectedProductType === CampaignProductType.SearchAuction &&
        Boolean(campaignOptions?.auctionProductData?.auctionCampaign);

    const isNextDisabled = (() => {
        if (isFirstStep) {
            return !isPromotionObjectStepValid;
        }
        if (isSecondStep) {
            return (
                selectedProductType === null ||
                !campaignOptions?.productTypes.includes(selectedProductType) ||
                isAuctionWarning
            );
        }
        return false;
    })();

    const progressConfig = getCampaignCreationWizardProgress({
        activeStep,
        campaignTarget,
        vacanciesCount: vacancies.length,
        vacanciesIsLoading,
        isFiltered,
        selectedVacancyId,
        selectedProductType,
        selectedCpcMode: selectedCpcMode as CampaignCreationWizardCpcMode | null,
    });
    const progress = progressConfig.visible ? (
        <ProgressBar
            progress={progressConfig.progress}
            segments={progressConfig.segments}
            data-qa="campaign-creation-wizard-progress"
        />
    ) : undefined;
    const primaryActions = (
        <>
            {!isFirstStep && (
                <Button mode="secondary" style="accent" onClick={handleBackClick} data-qa="ad-create-wizard-back">
                    {trl(TrlKeys.back)}
                </Button>
            )}
            <Button
                mode="primary"
                style="accent"
                onClick={handleNext}
                disabled={isNextDisabled || campaignOptionsIsLoading}
                loading={isNextLoading || campaignOptionsIsLoading}
                data-qa="ad-create-wizard-next"
            >
                {isSecondStep && isSelectedProductActive ? trl(TrlKeys.goToEdit) : trl(TrlKeys.next)}
            </Button>
        </>
    );
    const secondaryActions = (!isXS || isFirstStep) && (
        <Button mode="secondary" style="accent" onClick={handleToAdsClick} data-qa="ad-create-wizard-to-ads">
            {trl(TrlKeys.toAds)}
        </Button>
    );

    if (progress) {
        return (
            <ActionBar
                type={isXS ? 'vertical' : 'auto'}
                isSticky
                showProgress={progress}
                additionalContent={isMobile ? <div className={styles.progress}>{progress}</div> : null}
                primaryActions={primaryActions}
                secondaryActions={secondaryActions}
            />
        );
    }

    return (
        <ActionBar
            type={isXS ? 'vertical' : 'auto'}
            isSticky
            showDivider="always"
            primaryActions={primaryActions}
            secondaryActions={secondaryActions}
        />
    );
};
